/* eslint-disable react-native/no-inline-styles */
/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import { Text, View, StyleSheet, ScrollView } from 'react-native';
import DeviceInfo from 'react-native-device-info';

import { GridBox, ElementBox, Element, width, notchTop, topOffsetForPosition, scaleFactor } from '@components/frame';
import ColorWrapper from '@styles/colors';
import Selector from '@styles/selectors';
import { isPhone, isWeb, isAndroid, isIOS } from '@helpers';

import AnimatedHeader from '@components/common/AnimatedHeader';

import Information from './devInfo';
import Constraints from './constraints';
import Typography from './fonts';
import Grid from './grid';
import Buttons from './buttons';
import List from './list/open';
import Icon from './icon';
import Shadow from './shadow';
import Time from './time';
import Environment from './environment';
import Translation from './translation/Translation';
import Network from './network';
import ErrorBoundaryDemo from './errorBoundaryDemo';

import useStatusBar from '@components/common/useStatusBar';

const DevContent = (props) => {
  const { C, scale, selectedTab, setTab } = props;
  useEffect(() => {
    (async () => {
      const d = await DeviceInfo.getDeviceName();
      setDeviceName(d);
    })();
  }, []);

  const [deviceName, setDeviceName] = useState('');

  const setSelectedTab = (t) => {
    setTab(t);
  };

  const tabs = [
    {
      name: 'Account',
      key: 'environment',
    },
    {
      name: 'Device',
      key: 'devInfo',
    },
    {
      name: 'Network',
      key: 'network',
    },
    {
      name: 'Constraints',
      key: 'constraints',
    },
    {
      name: 'Grids',
      key: 'grids',
    },
    {
      name: 'Fonts',
      key: 'typography',
    },
    {
      name: 'Buttons',
      key: 'buttons',
    },
    {
      name: 'List',
      key: 'list',
    },
    {
      name: 'Icons',
      key: 'icon',
    },
    {
      name: 'Shadow',
      key: 'shadow',
    },
    {
      name: 'Time',
      key: 'time',
    },
    {
      name: 'Translation',
      key: 'translation',
    },
    {
      name: 'Error Boundary',
      key: 'errorBoundary',
    },
  ];

  const renderComponent = () => {
    switch (selectedTab) {
      case 'devInfo':
        return <Information {...{ scale }} />;
      case 'environment':
        return <Environment {...{ scale }} />;
      case 'constraints':
        return <Constraints {...{ scale }} />;
      case 'typography':
        return <Typography {...{ scale }} />;
      case 'buttons':
        return <Buttons {...{ scale }} />;
      case 'list':
        return <List {...{ scale }} />;
      case 'icon':
        return <Icon {...{ scale, C }} />;
      case 'shadow':
        return <Shadow {...{ scale, C }} />;
      case 'time':
        return <Time {...{ scale, C }} />;
      case 'translation':
        return <Translation {...{ scale, C }} />;
      case 'network':
        return <Network {...{ scale, C }} />;
      case 'errorBoundary':
        return <ErrorBoundaryDemo {...{ scale, C }} />;
      default:
        return <Grid {...{ scale }} />;
    }
  };

  const margin = (() => {
    if (isAndroid) {
      return isPhone ? topOffsetForPosition : 0;
    }
    if (isIOS) {
      return notchTop > 0 ? notchTop : 0;
    }

    return 0;
  })();

  return (
    <ElementBox>
      <AnimatedHeader {...{ text: 'Developer Menu', C, animated: false, back: true, backText: 'Settings' }} />
      <Element
        {...{
          config: {
            position: {
              top: 64,
              right: 18,
            },
            height: 20,
          },
          configMobile: {
            position: {
              top: 28,
              right: 16,
            },
          },
        }}
      >
        <Text
          style={{
            color: C('Label / Light / Secondary'),
            fontSize: scale(14),
          }}
        >
          {deviceName}
        </Text>
      </Element>
      <Element
        {...{
          config: {
            position: {
              bottom: 0,
            },
            width: width,
            height: 1,
            horizontalCenter: 0,
            backgroundColor: '#D256E6',
            opacity: 0,
          },
          configMobile: {
            opacity: 1,
          },
        }}
      />
      <Element
        {...{
          config: {
            position: {
              top: 84,
              right: 18,
            },
          },
          configMobile: {
            position: {
              right: 16,
              top: 48,
            },
          },
        }}
      >
        <Text
          style={{
            color: C('Label / Light / Secondary'),
            fontSize: scale(13),
          }}
        >
          {DeviceInfo.getVersion()}
        </Text>
      </Element>
      <Element
        {...{
          config: {
            position: {
              top: 95,
            },
            horizontalCenter: 0,
            width: width,
            height: 52,
            zIndex: 1000000,
            flexDirection: 'row',
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
          },
          configMobile: {
            position: {
              top: (isWeb ? 41 : 55) + margin / scaleFactor,
            },
            backgroundColor: C('Background/Light/Field [mobile]'),
            borderBottomColor: C('Hairline/Light/No trans'),
            borderBottomWidth: StyleSheet.hairlineWidth,
          },
        }}
      >
        <ScrollView
          horizontal={true}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={!isPhone && { width: '100%', justifyContent: 'center' }}
        >
          <View
            style={[
              {
                paddingLeft: scale(40),
                paddingRight: scale(40),
                flexDirection: 'row',
                display: 'flex',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                height: '100%',
              },
              !isPhone && { width: width },
            ]}
          >
            {tabs.map((item) => (
              <Selector
                onPress={() => setSelectedTab(item.key)}
                key={item.key}
                {...{ selected: item.key === selectedTab, variant: 'L', text: item.name }}
              />
            ))}
          </View>
        </ScrollView>
      </Element>
      {renderComponent()}
    </ElementBox>
  );
};

const DevInfo = (props) => {
  const { C } = props;
  const [selectedTab, setTab] = useState('environment');
  useStatusBar({ C });

  return (
    <GridBox
      {...{
        backgroundColor: C('Background / Light / Detail [mobile]'),
        headerOffset: true,
        notchOffset: false,
        sliderOffset: false,
      }}
    >
      <DevContent {...{ ...props, selectedTab, setTab }} />
    </GridBox>
  );
};

export default ColorWrapper(DevInfo);
